import React, { useEffect, useMemo } from 'react'
import jstz from 'jstz'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'

import {
    ReportDeliverySchedule,
    useCompanyCreateReportDeliveryScheduleMutation,
    useCompanyUpdateReportDeliveryScheduleMutation,
    useReportGetReportsForAdminQuery,
} from '../../../common/redux/api/web-api'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import Toggle from '../../../common/components/Toggle'
import GroupedButton from '../../../common/components/GroupedButton'
import useText from '../../../common/hooks/useText'
import { Input } from '../../../common/uikit/Input'
import { RootState } from '../../redux/store'
import { Dropdown } from '../../../common/uikit/Dropdown'

const defaultValues: ReportDeliverySchedule = {
    deliveryType: 'Email',
}

type AddReportDeliveryModalProps = {
    visible: boolean
    onHide: () => void
    selectedReportDeliverySchedule?: ReportDeliverySchedule
}

export const AddReportDeliveryModal = ({
    visible,
    onHide,
    selectedReportDeliverySchedule,
}: AddReportDeliveryModalProps) => {
    const text = useText()
    const { selectedCompanyId } = useSelector((state: RootState) => state.user)

    const reportDeliveryIntervalsList = [
        { value: 'Day', label: text('lblDayInterval') },
        { value: 'Month', label: text('lblMonthInterval') },
        { value: 'Year', label: text('lblYearInterval') },
        { value: 'Quarter', label: text('lblQuarterInterval') },
    ]

    const reportDeliverySchema = yup.object().shape({
        reportId: yup.number().nullable().required(text('errReportRequired')),
        deliveryInterval: yup.string().nullable().required(text('errDeliveryIntervalRequired')),
        deliveryType: yup.string().nullable().required(text('errDeliveryTypeRequired')),

        receiverName: yup.string().when('deliveryType', {
            is: 'Email',
            then: yup.string().required(text('errReceiverNameRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),
        receiverEmail: yup
            .string()
            .nullable()
            .when('deliveryType', {
                is: 'Email',
                then: yup.string().email(text('errReceiverEmailInvalid')).required(text('errReceiverEmailRequired')),
                otherwise: yup.string().nullable().notRequired(),
            }),
        mailSubject: yup.string().when('deliveryType', {
            is: 'Email',
            then: yup.string().required(text('errMailSubjectRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),

        ftpUrl: yup.string().when('deliveryType', {
            is: 'Ftp',
            then: yup.string().url(text('errFtpUrlInvalid')).required(text('errFtpUrlRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),
        ftpUserName: yup.string().when('deliveryType', {
            is: 'Ftp',
            then: yup.string().required(text('errFtpUserNameRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),
        ftpPassword: yup.string().when('deliveryType', {
            is: 'Ftp',
            then: yup.string().required(text('errFtpPasswordRequired')),
            otherwise: yup.string().nullable().notRequired(),
        }),

        active: yup.boolean().notRequired(),
    })

    const {
        control,
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors, isValid, isSubmitting },
    } = useForm<ReportDeliverySchedule>({
        defaultValues: {
            ...defaultValues,
            ...selectedReportDeliverySchedule,
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(reportDeliverySchema),
    })

    useEffect(() => {
        if (visible) {
            reset({ ...defaultValues, ...selectedReportDeliverySchedule })
        }
    }, [visible, selectedReportDeliverySchedule, reset])

    const deliveryType = watch('deliveryType')

    const [updateReport] = useCompanyUpdateReportDeliveryScheduleMutation()
    const [createReport] = useCompanyCreateReportDeliveryScheduleMutation()

    const saveReportDelivery = async (formValues: ReportDeliverySchedule) => {
        try {
            const payload = {
                ...formValues,
                ftpUrl: deliveryType === 'Ftp' ? formValues.ftpUrl : undefined,
                ftpUserName: deliveryType === 'Ftp' ? formValues.ftpUserName : undefined,
                ftpPassword: deliveryType === 'Ftp' ? formValues.ftpPassword : undefined,
                receiverName: deliveryType === 'Email' ? formValues.receiverName : undefined,
                receiverEmail: deliveryType === 'Email' ? formValues.receiverEmail : undefined,
                mailSubject: deliveryType === 'Email' ? formValues.mailSubject : undefined,
                timeZone: jstz.determine().name(),
            }

            if (selectedReportDeliverySchedule) {
                await updateReport({
                    companyId: selectedCompanyId,
                    reportDeliverySchedule: payload,
                }).unwrap()
            } else {
                await createReport({
                    companyId: selectedCompanyId,
                    reportDeliverySchedule: payload,
                }).unwrap()
            }

            onHide()
            notifySuccess(text('msgReportSaved'))
        } catch (_) {
            notifyError(text('msgReportNotSaved'))
        }
    }

    const { data: reports } = useReportGetReportsForAdminQuery({ selectedCompanyId }, { skip: !selectedCompanyId })
    const reportsOptions = useMemo(
        () =>
            reports?.map((nextReport) => ({
                value: nextReport.id,
                label: nextReport.name,
            })) || [],
        [reports]
    )

    return (
        <Dialog
            header={text('lblReportAddingModal')}
            visible={visible}
            onHide={onHide}
            style={{ width: '50vw' }}
            breakpoints={{ '960px': '75vw', '641px': '100vw' }}
            footer={
                <div>
                    <Button label={text('btnCancel')} onClick={onHide} icon="pi pi-times" />
                    <Button
                        label={text('btnSave')}
                        onClick={handleSubmit(saveReportDelivery)}
                        icon="pi pi-check"
                        disabled={!isValid || isSubmitting}
                        loading={isSubmitting}
                    />
                </div>
            }
        >
            <form className="flex flex-column gap-4">
                <div className="field">
                    <Controller
                        name="reportId"
                        control={control}
                        render={({ field }) => (
                            <Dropdown
                                options={reportsOptions}
                                id="report-id-select"
                                data-testid="report-id-select"
                                label={text('lblReport')}
                                value={field.value}
                                onChange={(option) => field.onChange(option.value)}
                                error={errors.reportId?.message}
                            />
                        )}
                    />
                </div>
                <div className="field">
                    <Controller
                        name="deliveryInterval"
                        control={control}
                        render={({ field }) => (
                            <Dropdown
                                options={reportDeliveryIntervalsList}
                                label={text('lblDeliveryIntervalField')}
                                id="interval-select"
                                data-testid="interval-select"
                                value={field.value}
                                onChange={(option) => field.onChange(option.value)}
                                error={errors.deliveryInterval?.message}
                            />
                        )}
                    />
                </div>
                <div className="field">
                    <label className="p-label" data-testid="report-delivery-label">
                        {text('lblDeliveryType')}
                    </label>

                    <Controller
                        name="deliveryType"
                        control={control}
                        render={({ field }) => (
                            <GroupedButton
                                options={[
                                    { value: 'Email', label: 'Email' },
                                    { value: 'Ftp', label: 'Ftp' },
                                ]}
                                onChange={(value) => field.onChange(value)}
                                value={field.value}
                            />
                        )}
                    />
                </div>

                <div className="mb-2 text-xl text-color-secondary">{text('lblReportDeliveryettings')}</div>
                {deliveryType === 'Email' ? (
                    <div className="flex flex-column gap-3" key="email">
                        <Input
                            id="receiverName"
                            label="Receiver Name"
                            maxLength={50}
                            error={errors.receiverName?.message}
                            {...register('receiverName')}
                        />
                        <Input
                            id="receiverEmail"
                            label="Receiver Email"
                            type="email"
                            autoComplete="off"
                            maxLength={100}
                            error={errors.receiverEmail?.message}
                            {...register('receiverEmail')}
                        />
                        <Input
                            id="receiverSubject"
                            label="Email Subject"
                            autoComplete="off"
                            maxLength={50}
                            error={errors.mailSubject?.message}
                            {...register('mailSubject')}
                        />
                    </div>
                ) : (
                    <div className="flex flex-column gap-3" key="ftp">
                        <Input
                            id="ftpURL"
                            label="FTP URL"
                            type="url"
                            error={errors.ftpUrl?.message}
                            {...register('ftpUrl')}
                        />
                        <Input
                            id="ftpUserName"
                            label="FTP Username"
                            autoComplete="new-password"
                            error={errors.ftpUserName?.message}
                            {...register('ftpUserName')}
                        />
                        <Input
                            id="ftpPassword"
                            label="FTP Password"
                            type="password"
                            autoComplete="new-password"
                            error={errors.ftpPassword?.message}
                            {...register('ftpPassword')}
                        />
                    </div>
                )}

                <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                        <Toggle
                            toggleText="Active"
                            isToggled={field.value ?? false}
                            className="justify-content-end"
                            toggleFn={() => field.onChange(!field.value)}
                        />
                    )}
                />
            </form>
        </Dialog>
    )
}
